import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X, Check } from "lucide-react";
import { Reveal, SectionLabel, useScrollTo, EASE } from "./Reveal";

const WITHOUT = [
  "Months lost figuring out kitchen design alone",
  "Hiring, training and managing kitchen staff yourself",
  "Swiggy / Zomato aggregator onboarding chaos",
  "Inconsistent output across shifts and people",
  "Capital locked into the wrong equipment",
  "You run the kitchen AND the brand",
];

const WITH = [
  "One partner for kitchen, operations and aggregators",
  "Trained kitchen manpower, managed for you",
  "Aggregator management handled end-to-end",
  "SOP-driven consistency from day one",
  "Right-sized equipment and layout decisions",
  "You focus on growth, brand building and NPDs",
];

const StartWithSkope = () => {
  const [mode, setMode] = useState("with");
  const scrollTo = useScrollTo();
  const items = mode === "with" ? WITH : WITHOUT;

  return (
    <section id="start-with-skope" className="relative bg-white py-28 lg:py-40 overflow-hidden" data-testid="start-with-skope-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <Reveal><SectionLabel>The Skope Way — 05</SectionLabel></Reveal>
        <div className="mt-6 grid lg:grid-cols-2 gap-10 lg:items-end">
          <Reveal delay={0.1}>
            <h2 className="font-display font-semibold text-3xl lg:text-5xl tracking-tight text-ink leading-[1.08]" data-testid="start-with-skope-headline">
              THE EASIEST WAY TO START IN THE FOOD INDUSTRY.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-ink/55 leading-relaxed max-w-xl text-base lg:text-lg">
              Skope Kitchens is India's first and only end-to-end kitchen operations and
              aggregator management support organisation. We take on the complete operational
              complexity of your food brand — so you can focus on <span className="text-ink">growth</span>,{" "}
              <span className="text-ink">brand building</span> and <span className="text-skope">new product development</span>.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.25}>
          <div className="mt-14 flex border border-ink/15 w-fit" data-testid="mode-toggle">
            {[
              { key: "without", label: "Without Skope" },
              { key: "with", label: "With Skope" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setMode(t.key)}
                data-testid={`mode-${t.key}`}
                className={`px-6 lg:px-8 py-3.5 font-mono text-[11px] tracking-[0.2em] uppercase transition-colors duration-300 ${
                  mode === t.key ? "bg-skope text-ink" : "text-ink/50 hover:text-ink"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-8 border border-ink/10 bg-[#f6f6f6]/60 min-h-[340px]">
          <AnimatePresence mode="wait">
            <motion.ul
              key={mode}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="grid md:grid-cols-2 gap-px bg-ink/5"
              data-testid="mode-list"
            >
              {items.map((item, i) => (
                <li key={item} className="flex items-start gap-4 bg-white p-6 lg:p-8">
                  <span className={`w-8 h-8 border flex items-center justify-center shrink-0 ${
                    mode === "with" ? "border-skope/50" : "border-red-400/40"
                  }`}>
                    {mode === "with" ? <Check size={14} className="text-skope" /> : <X size={14} className="text-red-400/80" />}
                  </span>
                  <div>
                    <span className="font-mono text-[10px] text-ink/30 tracking-[0.2em]">0{i + 1}</span>
                    <p className={`mt-1 text-sm lg:text-base leading-relaxed ${mode === "with" ? "text-ink/80" : "text-ink/45 line-through decoration-ink/20"}`}>
                      {item}
                    </p>
                  </div>
                </li>
              ))}
            </motion.ul>
          </AnimatePresence>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <button
              onClick={() => scrollTo("#contact")}
              className="group flex items-center justify-center gap-3 bg-skope text-ink font-mono text-xs tracking-[0.2em] uppercase px-10 py-5 hover:bg-ink hover:text-white transition-colors duration-300"
              data-testid="start-with-skope-cta"
            >
              Start With Skope
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <p className="font-mono text-[11px] tracking-[0.15em] text-ink/40 uppercase">
              Kitchen + Manpower + Aggregators + SOPs — one partner.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default StartWithSkope;
